import React, { useEffect, createContext, useContext, useState } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { FightDataProvider } from './contexts/FightDataContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Header from './components/Header';
import AnimatedRoutes from './components/AnimatedRoutes';
import { addTestPastEvent } from './services/predictionTrackingService';
import { getRouteMetadata, updateMetaTags } from './utils/seo';
import { logger } from './utils/logger';
import { analytics } from './utils/analytics';

// Create context for intro animation state
const IntroContext = createContext<{
  showingIntro: boolean;
  setShowingIntro: (showing: boolean) => void;
}>({
  showingIntro: false,
  setShowingIntro: () => {},
});

export const useIntro = () => useContext(IntroContext);

// Add to window for testing
declare global {
  interface Window {
    addTestPastEvent?: () => void;
  }
}

if (process.env.NODE_ENV === 'development') {
  window.addTestPastEvent = addTestPastEvent;
}

// API connectivity test component - used for background checking only
const ApiTest = () => {
  useEffect(() => {
    const testApi = async () => {
      try {
        logger.log('Testing API connectivity...');
        const response = await fetch('https://fight-vision-backend-production.up.railway.app/api/health');
        logger.log('API Response:', response);
        
        if (response.ok) {
          const data = await response.json();
          logger.log('API Data:', data);
          analytics.trackApiSuccess('/api/health');
        } else {
          logger.error('API error:', response.status);
          analytics.trackApiError(new Error(`API returned status: ${response.status}`));
        }
      } catch (error) {
        logger.error('API connection error:', error);
        analytics.trackApiError(error instanceof Error ? error : new Error(String(error)));
      }
    };

    testApi();
  }, []);

  // No longer rendering the visible component, just running the check
  return null;
};

// SEO wrapper component
const SEOWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    const metadata = getRouteMetadata(location.pathname);
    updateMetaTags(metadata);
    
    // Track page view
    analytics.trackPageView(location.pathname);

    // Also track as screen view for GA4
    analytics.trackEvent('screen_view', {
      app_name: 'Fight Vision',
      screen_name: location.pathname
    });
  }, [location]);

  return <>{children}</>;
};

const App = () => {
  const [showingIntro, setShowingIntro] = useState(() => {
    // Only show intro if we haven't seen it before
    return localStorage.getItem('hasSeenIntro') !== 'true';
  });

  useEffect(() => {
    // Initialize analytics with user properties
    analytics.init();
  }, []);

  return (
    <ThemeProvider>
      <FightDataProvider>
        <IntroContext.Provider value={{ showingIntro, setShowingIntro }}>
          <Router>
            <SEOWrapper>
              <div className="flex flex-col min-h-screen bg-background dark:bg-dark-background text-text-primary dark:text-dark-text-primary">
                {/* ApiTest is still present but no longer displays anything */}
                <ApiTest />
                <div className="mx-auto w-full max-w-[1800px] 2xl:border-x border-border dark:border-dark-border min-h-screen">
                  {!showingIntro && (
                    <Header className="animate-fade-in" />
                  )}
                  <div className="flex-1">
                    <AnimatedRoutes />
                  </div>
                </div>
              </div>
            </SEOWrapper>
          </Router>
        </IntroContext.Provider>
      </FightDataProvider>
    </ThemeProvider>
  );
};

export default App;
